import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import PopupForm from '../components/PopupForm';
import Chatbot from '../components/Chatbot';
import programsData from '../data/programs.json';
import './cap.css';

const capTrackOptions = [
	{
		id: 'emerging-ai-engineer',
		buttonLabel: 'Emerging AI Engineer',
		audience: 'FOR CAREER STARTERS & EARLY-CAREER DEVS',
		title: 'Emerging AI Engineer',
		description:
			"You're building your foundation. This track gets you fluent in AI-assisted development, prompt engineering, and modern engineering workflows, then helps you ship visible proof and start showing up in the right communities. You stop feeling behind and start compounding.",
		route:
			'Your roadmap likely routes into: GitHub Copilot (GH-300) certification and the cloud foundation bundles.',
		focusAreas: ['AI coding workflow mastery', 'Prompting and tool fluency', 'Portfolio-grade shipped projects'],
		quarterPlan: [
			{
				month: 'MONTH 1',
				title: 'Accelerate with AI',
				items: [
					'AI-assisted development and prompt engineering',
					'GitHub Copilot and Claude workflows',
					'5W Framework workshop for context engineering',
					'Practice: AI productivity fundamentals',
					'Share: weekly LinkedIn learning posts via LWT',
				],
			},
			{
				month: 'MONTH 2',
				title: 'Build with confidence',
				items: [
					'Human-in-the-loop engineering',
					'AI-assisted debugging and testing',
					'Vibe Sprint workshop: build 2-3 mini projects',
					'Practice: vibe engineering deep dive',
					'Share: mentor peers on prompts and workflows',
				],
			},
			{
				month: 'MONTH 3',
				title: 'Step toward leadership',
				items: [
					'Intro to agentic engineering concepts',
					'AI governance and validation',
					'Review and consolidate: close weak areas',
					'Practice: present a learning session',
					'Share: present at a Lead With Tech event',
				],
			},
		],
	},
	{
		id: 'ai-driven-solution-engineer',
		buttonLabel: 'AI-Driven Solution Engineer',
		audience: 'FOR MID-LEVEL ENGINEERS READY TO LEAD DELIVERY',
		title: 'AI-Driven Solution Engineer',
		description:
			'This track helps you move from task execution to end-to-end solution ownership. You design with AI in the loop, choose the right stack for constraints, and ship solutions that solve business problems faster and with higher quality.',
		route:
			'Your roadmap likely routes into: advanced Copilot implementation, full-stack acceleration, and solution architecture practice.',
		focusAreas: ['System and feature decomposition', 'AI-accelerated delivery patterns', 'Stakeholder-ready communication'],
		quarterPlan: [
			{
				month: 'MONTH 1',
				title: 'Design better systems',
				items: [
					'Solution framing from business outcomes',
					'Architecture patterns for AI-enabled products',
					'Hands-on API and integration planning',
					'Practice: build one production-grade module',
					'Share: design review with peers',
				],
			},
			{
				month: 'MONTH 2',
				title: 'Ship for production',
				items: [
					'Reliability, testing, and observability practices',
					'Copilot-assisted refactoring at scale',
					'Workflow automation for delivery speed',
					'Practice: execute a delivery sprint',
					'Share: walkthrough of tradeoff decisions',
				],
			},
			{
				month: 'MONTH 3',
				title: 'Lead cross-functional delivery',
				items: [
					'Lead planning with product and design partners',
					'Risk management and stakeholder communication',
					'Post-release analysis and iteration loops',
					'Practice: run end-to-end release governance',
					'Share: publish a delivery case study',
				],
			},
		],
	},
	{
		id: 'ai-architect-transformation-leader',
		buttonLabel: 'AI Architect & Transformation Leader',
		audience: 'FOR SENIOR ENGINEERS, LEADS, AND ASPIRING ARCHITECTS',
		title: 'AI Architect & Transformation Leader',
		description:
			'You are operating at architecture and org influence level. This track sharpens your ability to define AI adoption strategy, modernize delivery culture, and lead cross-functional teams through platform and capability transformation.',
		route:
			'Your roadmap likely routes into: architect acceleration, transformation playbooks, and enterprise AI implementation paths.',
		focusAreas: ['Architecture decision leadership', 'Transformation strategy and enablement', 'Execution governance across teams'],
		quarterPlan: [
			{
				month: 'MONTH 1',
				title: 'Set transformation direction',
				items: [
					'Current-state architecture and capability mapping',
					'AI adoption roadmap by business value',
					'Operating model design for platform teams',
					'Practice: define transformation charter',
					'Share: leadership alignment session',
				],
			},
			{
				month: 'MONTH 2',
				title: 'Enable teams at scale',
				items: [
					'Architecture governance and decision frameworks',
					'Developer experience and guardrail design',
					'Platform strategy for velocity and compliance',
					'Practice: run architecture review boards',
					'Share: mentor leads across teams',
				],
			},
			{
				month: 'MONTH 3',
				title: 'Drive measurable outcomes',
				items: [
					'KPI framework for transformation progress',
					'Change communication and adoption loops',
					'Portfolio prioritization for sustained impact',
					'Practice: executive review and recalibration',
					'Share: publish architecture playbook',
				],
			},
		],
	},
];

const capHeroMenuItems = [
	{ label: 'The Method', targetId: 'cap-v2-method-title' },
	{ label: 'Tracks', targetId: 'cap-v2-track-title' },
	{ label: 'What You Build', targetId: 'cap-v2-quarter-title' },
	{ label: 'Pricing', targetId: 'cap-v2-enroll-title' },
	{ label: 'FAQ', targetId: 'cap-v2-faq-title' },
];

function Cap() {
	const [activeTrackId, setActiveTrackId] = useState(capTrackOptions[0].id);
	const [isLeadPopupOpen, setIsLeadPopupOpen] = useState(false);
	const [leadPopupContext, setLeadPopupContext] = useState({
		formType: 'register_now',
		sourceKey: 'cap_hero_enroll',
		interestKey: 'career_authoring_program',
	});
	const activeTrack = capTrackOptions.find((track) => track.id === activeTrackId) || capTrackOptions[0];

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'instant' });
	}, []);

	const handleHeroMenuClick = (event, targetId) => {
		event.preventDefault();
		const targetElement = document.getElementById(targetId);

		if (targetElement) {
			targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	const openLeadPopup = (sourceKey, formType = 'register_now') => {
		setLeadPopupContext({
			formType,
			sourceKey,
			interestKey: 'career_authoring_program',
		});
		setIsLeadPopupOpen(true);
	};

	return (
		<>
			<Header />
			<main className="cap-page cap-page-v2">
			<div className="cap-v2-toolbar-sticky">
				<nav className="cap-v2-toolbar" aria-label="Career Authoring Program quick navigation">
					<ul className="cap-v2-toolbar-list">
						{capHeroMenuItems.map((item) => (
							<li key={item.targetId}>
								<a
									href={`#${item.targetId}`}
									onClick={(event) => handleHeroMenuClick(event, item.targetId)}
									className="cap-v2-toolbar-link"
								>
									{item.label}
								</a>
							</li>
						))}
					</ul>
				</nav>
			</div>

			<section className="cap-v2-hero" aria-labelledby="cap-v2-title">
				<div className="cap-v2-hero-inner">
					<div className="cap-v2-pill">Founding Cohort · 15 seats · Starts soon</div>

					<h1 id="cap-v2-title" className="cap-v2-title">
						Author your career.
						<br />
						Or wait to be <span>rewritten.</span>
					</h1>

					<p className="cap-v2-subtitle">
						In the AI era, technical skill alone won&apos;t keep you relevant. The Career
						Authoring Program is a 3-month live cohort that turns where you are now into
						a clear, AI-ready roadmap and gets you executing the first quarter of it with
						Chaitanya in the room.
					</p>

					<div className="cap-v2-actions">
						<button
							className="cap-v2-btn cap-v2-btn-primary"
							type="button"
							onClick={() => openLeadPopup('cap_hero_enroll', 'register_now')}
						>
							Enroll in the founding cohort →
						</button>
						<button className="cap-v2-btn cap-v2-btn-secondary" type="button">
							See how it works
						</button>
					</div>

					<ul className="cap-v2-meta" aria-label="Program highlights">
						<li><strong>3 months</strong> · live cohort</li>
						<li><strong>In-person</strong> Bangalore + online</li>
						<li><strong>Led by</strong> Chaitanya Allidona</li>
					</ul>
				</div>
			</section>

			<section className="cap-v2-problem" aria-labelledby="cap-v2-problem-title">
				<div className="cap-v2-problem-grid">
					<div className="cap-v2-problem-copy">
						<p className="cap-v2-problem-eyebrow">THE REAL PROBLEM</p>
						<h2 id="cap-v2-problem-title" className="cap-v2-problem-title">
							Knowing how to code is no longer the moat.
						</h2>

						<p className="cap-v2-problem-text">
							AI now writes the boilerplate, drafts the tests, and explains the legacy
							code. The engineers pulling ahead aren&apos;t the ones who resist that.
							They&apos;re the ones who decide, on purpose, what to become next and build a
							plan to get there.
						</p>

						<p className="cap-v2-problem-text">
							Most developers don&apos;t have a plan. They have a job, a vague worry about
							being left behind, and a feed full of conflicting advice about which tool
							to learn this week.
						</p>

						<blockquote className="cap-v2-problem-quote">
							A career without a plan is a career someone else gets to write for you.
						</blockquote>
					</div>

					<div className="cap-v2-problem-cards" aria-label="Core capability shifts">
						<article className="cap-v2-problem-card">
							<h3>Skill, not certainty</h3>
							<p>
								The half-life of a technical skill keeps shrinking. The ability to learn,
								apply, and adapt is the only durable asset.
							</p>
						</article>

						<article className="cap-v2-problem-card">
							<h3>Human + AI</h3>
							<p>
								Relevance now comes from combining domain expertise, communication, and AI
								tools, not from any single one.
							</p>
						</article>

						<article className="cap-v2-problem-card">
							<h3>Direction beats speed</h3>
							<p>
								Most upskilling fails because it has no destination. A roadmap turns
								scattered effort into compounding progress.
							</p>
						</article>
					</div>
				</div>
			</section>

			<section className="cap-v2-outcomes" aria-labelledby="cap-v2-outcomes-title">
				<div className="cap-v2-outcomes-head">
					<p className="cap-v2-outcomes-eyebrow">WHAT YOU WALK AWAY WITH</p>
					<h2 id="cap-v2-outcomes-title" className="cap-v2-outcomes-title">
						You don&apos;t leave with notes. You leave with a plan you&apos;re already running.
					</h2>
					<p className="cap-v2-outcomes-subtitle">
						The Career Authoring Program is not a course you consume. It&apos;s a system you
						build around your own career, with accountability to actually start it.
					</p>
				</div>

				<div className="cap-v2-outcomes-grid" aria-label="Program outcomes">
					<article className="cap-v2-outcome-card">
						<div className="cap-v2-outcome-icon" aria-hidden="true">R</div>
						<h3>Your personalized roadmap</h3>
						<p>
							A written Career Authoring Plan: your target AI-era role, your real gaps
							across technical, communication, behavioral, and collaboration skills,
							and the quarterly, monthly, and weekly goals to close them.
						</p>
					</article>

					<article className="cap-v2-outcome-card">
						<div className="cap-v2-outcome-icon" aria-hidden="true">Q1</div>
						<h3>The first quarter, executed</h3>
						<p>
							You don&apos;t just plan. Over 12 weeks you run the Learn-Practice-Share
							loop live, ship 2-3 AI-enabled projects, and build the habit that
							carries the rest of the year.
						</p>
					</article>

					<article className="cap-v2-outcome-card">
						<div className="cap-v2-outcome-icon" aria-hidden="true">FIT</div>
						<h3>Your exact next program</h3>
						<p>
							By the end you know precisely which LWT path closes your #1 gap,
							whether that&apos;s GitHub Copilot certification, the Architect Accelerator,
							or a cloud track. No guesswork, no generic advice.
						</p>
					</article>
				</div>
			</section>

			<section className="cap-v2-method" aria-labelledby="cap-v2-method-title">
				<div className="cap-v2-method-head">
					<p className="cap-v2-method-eyebrow">THE METHOD</p>
					<h2 id="cap-v2-method-title" className="cap-v2-method-title">
						One loop to master any skill. Five steps to author a career.
					</h2>
					<p className="cap-v2-method-subtitle">
						These aren&apos;t theory. They&apos;re the actual thinking tools used in every
						LWT session.
					</p>
				</div>

				<div className="cap-v2-method-loop-grid" aria-label="Learn practice share loop">
					<article className="cap-v2-method-card cap-v2-method-card--loop">
						<p className="cap-v2-method-step">01 · LEARN</p>
						<h3>Build the foundation</h3>
						<p>
							Continuous learning, research, and mentoring. Microsoft Learn, curated
							sources, and live LWT sessions, not random tutorials.
						</p>
					</article>

					<article className="cap-v2-method-card cap-v2-method-card--loop">
						<p className="cap-v2-method-step">02 · PRACTICE</p>
						<h3>Apply it for real</h3>
						<p>
							Hands-on execution through projects, vibe-coding sprints, 1:1 mentoring,
							and solving real-world challenges with feedback.
						</p>
					</article>

					<article className="cap-v2-method-card cap-v2-method-card--loop">
						<p className="cap-v2-method-step">03 · SHARE</p>
						<h3>Teach to cement it</h3>
						<p>
							Present at LWT events, write, and mentor peers. Sharing turns knowledge
							into mastery and builds your visible reputation.
						</p>
					</article>
				</div>

				<div className="cap-v2-method-plan-grid" aria-label="Five-step career authoring plan">
					<article className="cap-v2-method-card cap-v2-method-card--plan">
						<div className="cap-v2-method-badge" aria-hidden="true">1</div>
						<h3>Current &amp; Future Self</h3>
						<p>
							Get clear on your role today and the leadership role you&apos;re aiming at.
						</p>
					</article>

					<article className="cap-v2-method-card cap-v2-method-card--plan">
						<div className="cap-v2-method-badge" aria-hidden="true">2</div>
						<h3>Self-Assessment</h3>
						<p>
							Map strengths and gaps across technical, communication, behavioral,
							and collaboration.
						</p>
					</article>

					<article className="cap-v2-method-card cap-v2-method-card--plan">
						<div className="cap-v2-method-badge" aria-hidden="true">3</div>
						<h3>Research</h3>
						<p>
							Study AI-era roles, target companies, and the communities to engage.
						</p>
					</article>

					<article className="cap-v2-method-card cap-v2-method-card--plan">
						<div className="cap-v2-method-badge" aria-hidden="true">4</div>
						<h3>Break Down &amp; Plan</h3>
						<p>
							Convert gaps into measurable weekly, monthly, and quarterly goals.
						</p>
					</article>

					<article className="cap-v2-method-card cap-v2-method-card--plan">
						<div className="cap-v2-method-badge" aria-hidden="true">5</div>
						<h3>Adjust &amp; Achieve</h3>
						<p>
							Execute, get feedback, refine, and hit your goals with consistency.
						</p>
					</article>
				</div>
			</section>

			<section className="cap-v2-track" aria-labelledby="cap-v2-track-title">
				<div className="cap-v2-track-head">
					<p className="cap-v2-track-eyebrow">PICK YOUR TRACK</p>
					<h2 id="cap-v2-track-title" className="cap-v2-track-title">
						One program. Three doors. You enter where you are.
					</h2>
					<p className="cap-v2-track-subtitle">
						The plan you build is yours, but everyone starts on one of three
						acceleration tracks. Choose the one that fits you now.
					</p>
				</div>

				<div className="cap-v2-track-tabs" role="tablist" aria-label="Select your role track">
					{capTrackOptions.map((track) => (
						<button
							key={track.id}
							type="button"
							role="tab"
							aria-selected={activeTrack.id === track.id}
							className={`cap-v2-track-tab ${activeTrack.id === track.id ? 'is-active' : ''}`}
							onClick={() => setActiveTrackId(track.id)}
						>
							{track.buttonLabel}
						</button>
					))}
				</div>

				<article className="cap-v2-track-panel" role="tabpanel" aria-live="polite">
					<p className="cap-v2-track-audience">{activeTrack.audience}</p>
					<h3>{activeTrack.title}</h3>
					<p className="cap-v2-track-description">{activeTrack.description}</p>
					<div className="cap-v2-track-route">{activeTrack.route}</div>

					<div className="cap-v2-track-dedicated">
						<h4>Dedicated section to add</h4>
						<ul>
							{activeTrack.focusAreas.map((area) => (
								<li key={area}>{area}</li>
							))}
						</ul>
					</div>
				</article>
			</section>

			<section className="cap-v2-quarter" aria-labelledby="cap-v2-quarter-title">
				<div className="cap-v2-quarter-head">
					<p className="cap-v2-quarter-eyebrow">WHAT YOU ACTUALLY BUILD</p>
					<h2 id="cap-v2-quarter-title" className="cap-v2-quarter-title">
						A real quarter, broken down. Here&apos;s the {activeTrack.title} track.
					</h2>
					<p className="cap-v2-quarter-subtitle">
						Every track maps to the same rhythm: learn the right things, practice them
						on real work, share to lock them in. This is what your first three months
						look like.
					</p>
				</div>

				<div className="cap-v2-quarter-grid" aria-label="Three month execution plan">
					{activeTrack.quarterPlan.map((phase) => (
						<article className="cap-v2-quarter-card" key={phase.month}>
							<p className="cap-v2-quarter-month">{phase.month}</p>
							<h3>{phase.title}</h3>
							<ul>
								{phase.items.map((item) => (
									<li key={item}>{item}</li>
								))}
							</ul>
						</article>
					))}
				</div>
			</section>

			<section className="cap-v2-why" aria-labelledby="cap-v2-why-title">
				<div className="cap-v2-why-head">
					<p className="cap-v2-why-eyebrow">WHY LEAD WITH TECH</p>
					<h2 id="cap-v2-why-title" className="cap-v2-why-title">
						Live, in-person, and personally taught. No avatars, no pre-recorded fluff.
					</h2>
				</div>

				<div className="cap-v2-why-stats" aria-label="Program credibility metrics">
					<article className="cap-v2-why-stat-card">
						<h3>15</h3>
						<p>Seats per cohort, by design</p>
					</article>
					<article className="cap-v2-why-stat-card">
						<h3>10+</h3>
						<p>Events across 4 cities</p>
					</article>
					<article className="cap-v2-why-stat-card">
						<h3>5★</h3>
						<p>Average event rating</p>
					</article>
					<article className="cap-v2-why-stat-card">
						<h3>500+</h3>
						<p>Engineers in the community</p>
					</article>
				</div>

				<article className="cap-v2-why-quote">
					<div className="cap-v2-why-avatar" aria-hidden="true">CA</div>
					<div className="cap-v2-why-quote-content">
						<p>
							I show up to every session personally. No pre-recorded content, no outsourced
							instruction. Just the frameworks, the feedback, and the mentorship I wish
							I&apos;d had earlier in my own career. The cohort is capped at 15 so I can
							actually look at your plan, not a spreadsheet of usernames.
						</p>
						<footer>
							Chaitanya Allidona · Founder &amp; AI Productivity Trainer, Lead With Tech
						</footer>
					</div>
				</article>
			</section>

			<section className="cap-v2-enroll" aria-labelledby="cap-v2-enroll-title">
				<div className="cap-v2-enroll-head">
					<p className="cap-v2-enroll-eyebrow">ENROLL</p>
					<h2 id="cap-v2-enroll-title" className="cap-v2-enroll-title">
						Join the founding cohort
					</h2>
					<p className="cap-v2-enroll-subtitle">
						The first cohort is priced to fill. You get the same live program, the same
						personal mentorship, at the founding rate, in exchange for your feedback and
						results.
					</p>
				</div>

				<article className="cap-v2-enroll-card" aria-label="Founding cohort offer">
					<p className="cap-v2-enroll-badge">FOUNDING COHORT RATE</p>

					<p className="cap-v2-enroll-price">
						<span className="cap-v2-enroll-current">&#8377;9,999</span>
						<span className="cap-v2-enroll-original">&#8377;19,999</span>
					</p>

					<p className="cap-v2-enroll-caption">One flat fee · 3-month live cohort · no subscription</p>

					<ul className="cap-v2-enroll-list">
						<li>12 weekly live sessions (online Fridays + in-person Saturdays in Bangalore)</li>
						<li>A 1:1 assessment and your written Career Authoring Plan</li>
						<li>The Learn-Practice-Share system, run live with feedback</li>
						<li>2-3 AI-enabled projects shipped during the cohort</li>
						<li>A speaking slot at a Lead With Tech event</li>
						<li>Access to the LWT community and a clear next-program recommendation</li>
					</ul>

					<button
						className="cap-v2-enroll-cta"
						type="button"
						onClick={() => openLeadPopup('cap_pricing_reserve_seat', 'save_spot')}
					>
						Reserve my seat →
					</button>

					<p className="cap-v2-enroll-footnote">
						Only <strong>15 seats</strong>. Founding rate ends when the cohort fills.
					</p>
				</article>
			</section>

			<section className="cap-v2-faq" aria-labelledby="cap-v2-faq-title">
				<div className="cap-v2-faq-head">
					<p className="cap-v2-faq-eyebrow">QUESTIONS</p>
					<h2 id="cap-v2-faq-title" className="cap-v2-faq-title">
						Before you enroll
					</h2>
				</div>

				<div className="cap-v2-faq-list" role="list" aria-label="Enrollment FAQs">
					<article className="cap-v2-faq-item" role="listitem">
						<h3>Is this for me if I&apos;m a complete beginner / a senior architect?</h3>
						<p>
							Both. The program runs on three tracks, from Emerging AI Engineer to AI
							Architect, and your plan is built around your actual starting point. You
							enter where you are.
						</p>
					</article>

					<article className="cap-v2-faq-item" role="listitem">
						<h3>Live or recorded?</h3>
						<p>
							Live, every session. Chaitanya teaches personally. There&apos;s an online cadence
							plus in-person Saturdays in Bangalore. No pre-recorded content and no AI
							avatars.
						</p>
					</article>

					<article className="cap-v2-faq-item" role="listitem">
						<h3>How is this different from your certification courses?</h3>
						<p>
							Certifications teach a specific skill. Career Authoring teaches you what to
							learn next and why, then gets you executing. Most people take this first,
							then use their roadmap to pick the right cert or flagship.
						</p>
					</article>

					<article className="cap-v2-faq-item" role="listitem">
						<h3>What&apos;s the time commitment?</h3>
						<p>
							One live session per week over 3 months, plus practice and sharing between
							sessions. It&apos;s built to run alongside a full-time job.
						</p>
					</article>

					<article className="cap-v2-faq-item" role="listitem">
						<h3>Why only 15 seats?</h3>
						<p>
							So the mentorship is real. A capped cohort means Chaitanya reviews your plan
							and your work directly, not a dashboard of names.
						</p>
					</article>

					<article className="cap-v2-faq-item" role="listitem">
						<h3>Why is the founding rate so low?</h3>
						<p>
							It&apos;s the first cohort. You get the founding price; we get your honest
							feedback and results to build the program&apos;s track record. The rate goes
							to &#8377;19,999 after.
						</p>
					</article>
				</div>
			</section>

			<section className="cap-v2-final-cta" aria-labelledby="cap-v2-final-cta-title">
				<div className="cap-v2-final-cta-inner">
					<p className="cap-v2-final-cta-eyebrow">THE AI ERA DOESN&apos;T WAIT</p>
					<h2 id="cap-v2-final-cta-title" className="cap-v2-final-cta-title">
						Stop drifting. Start authoring.
					</h2>
					<p className="cap-v2-final-cta-subtitle">
						Fifteen seats, one founding cohort. Build the plan, run the first quarter,
						and walk out knowing exactly where you&apos;re headed.
					</p>
					<button
						className="cap-v2-final-cta-btn"
						type="button"
						onClick={() => openLeadPopup('cap_final_enroll_9999', 'register_now')}
					>
						Enroll for &#8377;9,999 →
					</button>
				</div>
			</section>

			<PopupForm
				isOpen={isLeadPopupOpen}
				onClose={() => setIsLeadPopupOpen(false)}
				formType={leadPopupContext.formType}
				sourceKey={leadPopupContext.sourceKey}
				interestKey={leadPopupContext.interestKey}
			/>
			</main>
			<Chatbot programsData={programsData} />
	 
		</>
	);
}

export default Cap;

